import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import './theme.css';
import './ui.css';

const PALETTE = [
  '#0c66e4', '#1f845a', '#c9372c', '#8270db', '#b38600',
  '#e56910', '#0e7490', '#ae4787', '#5e4db2', '#2b6e6e',
];

export const colorFor = (str = '') => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

export const initials = (name = '') => {
  const p = name.trim().split(/\s+/).filter(Boolean);
  if (!p.length) return '?';
  return ((p[0][0] || '') + (p[1] ? p[1][0] : p[0][1] || '')).toUpperCase();
};

const DEFAULT_IMG_MARK = 'encrypted-tbn0.gstatic.com';

export function Avatar({ name, src, size = 32, presence, title, style, className = '' }) {
  // The backend stores a placeholder photo for users with no picture; show initials instead.
  const hasImg = src && !src.includes(DEFAULT_IMG_MARK);
  return (
    <span
      className={`avatar ${className}`}
      title={title || name}
      style={{
        width: size,
        height: size,
        fontSize: Math.max(10, size * 0.4),
        background: hasImg ? 'var(--sunken)' : colorFor(name),
        ...style,
      }}
    >
      {hasImg ? <img src={src} alt={name} /> : initials(name)}
      {presence && <span className={`presence ${presence}`} />}
    </span>
  );
}

export function Button({ variant, size, icon, className = '', children, ...rest }) {
  const cls = [
    'btn',
    variant && `btn-${variant}`,
    size && `btn-${size}`,
    icon && 'btn-icon',
    className,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  );
}

export const Lozenge = ({ color, children }) => (
  <span className={`lozenge ${color || ''}`}>{children}</span>
);

export const Spinner = () => (
  <div className="center-fill">
    <div className="spinner" />
  </div>
);

export const Empty = ({ icon, title, children }) => (
  <div className="empty">
    {icon}
    <h4>{title}</h4>
    <div>{children}</div>
  </div>
);

const openModals = []; // stack of open dialogs: Escape closes only the top one
export function Modal({ title, onClose, children, footer, wide }) {
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    const id = Symbol('modal');
    openModals.push(id);
    const onKey = (e) => e.key === 'Escape' && openModals[openModals.length - 1] === id && closeRef.current && closeRef.current();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      openModals.splice(openModals.indexOf(id), 1);
    };
  }, []);
  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose && onClose()}>
      <div className={`modal ${wide ? 'wide' : ''}`} role="dialog" aria-modal="true">
        {title && (
          <div className="modal-head">
            <span>{title}</span>
          </div>
        )}
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

/** Anchored popover that closes on outside click / Escape. Wrap trigger + content.
 *  Rendered in a portal with fixed positioning so overflow/clipping of any ancestor
 *  (tables with overflow-x, sidebars, …) can never hide the menu. Flips up/down when
 *  there is no room and follows the trigger on scroll/resize. */
export function Popover({ trigger, children, align = 'left', up = false }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null); // { top, left } in viewport coordinates
  const wrapRef = useRef(null);
  const menuRef = useRef(null);

  const place = useCallback(() => {
    const el = wrapRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const menu = menuRef.current;
    const mh = menu ? menu.offsetHeight : 0;
    const mw = menu ? menu.offsetWidth : 0;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let top = up ? r.top - mh - 4 : r.bottom + 4;
    if (!up && top + mh > vh - 8 && r.top - mh - 4 > 8) top = r.top - mh - 4; // flip up
    if (up && top < 8 && r.bottom + 4 + mh < vh - 8) top = r.bottom + 4; // flip down
    top = Math.max(8, Math.min(top, vh - 8));
    let left = align === 'right' ? r.right - mw : r.left;
    left = Math.max(8, Math.min(left, vw - mw - 8));
    setPos({ top, left });
  }, [align, up]);

  useEffect(() => {
    if (!open) return undefined;
    const raf = requestAnimationFrame(place); // measure after the menu has mounted
    const onDown = (e) => {
      if (wrapRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true); // any container scroll, in capture phase
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, place]);

  return (
    <span ref={wrapRef} style={{ position: 'relative', display: 'inline-flex' }}>
      <span onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} style={{ display: 'inline-flex' }}>
        {typeof trigger === 'function' ? trigger(open) : trigger}
      </span>
      {open && pos &&
        createPortal(
          <div
            ref={menuRef}
            className="popover"
            style={{ position: 'fixed', top: pos.top, left: pos.left }}
            onClick={(e) => { e.stopPropagation(); if (e.target.closest('.menu-item')) setOpen(false); }}
          >
            {typeof children === 'function' ? children(() => setOpen(false)) : children}
          </div>,
          document.body
        )}
    </span>
  );
}

/* ---- Confirm dialog (system modal replacing window.confirm / window.prompt) ---- */
const ConfirmCtx = createContext({ confirm: () => Promise.resolve(false), ask: () => Promise.resolve(null) });
/** show a system confirm dialog: confirm({ title, body, okLabel, cancelLabel, danger, requireText, requireLabel }) -> Promise<boolean> */
export const useConfirm = () => useContext(ConfirmCtx).confirm;
/** show a system prompt dialog with a text input: ask({ title, label, okLabel, cancelLabel, danger, initial }) -> Promise<string|null> (null = cancelled) */
export const usePrompt = () => useContext(ConfirmCtx).ask;

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { title, body, okLabel, cancelLabel, danger, requireText, requireLabel, prompt, initial, value, resolve, resolvesValue }
  const close = useCallback((result) => {
    setState((s) => {
      if (s) s.resolve(result);
      return null;
    });
  }, []);
  const confirm = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setState({ danger: false, ...opts, value: '', resolve, resolvesValue: false });
      }),
    []
  );
  const ask = useCallback(
    (opts) =>
      new Promise((resolve) => {
        setState({ danger: false, ...opts, prompt: true, value: opts.initial || '', resolve, resolvesValue: true });
      }),
    []
  );
  const isPrompt = !!state && !!state.prompt;
  const canOk = !state || isPrompt || !state.requireText || state.value.trim() === state.requireText;
  return (
    <ConfirmCtx.Provider value={{ confirm, ask }}>
      {children}
      {state && (
        <Modal
          title={state.title}
          onClose={() => close(isPrompt ? null : false)}
          footer={
            <>
              <Button variant="subtle" onClick={() => close(isPrompt ? null : false)}>{state.cancelLabel || t('Cancel')}</Button>
              <Button variant={state.danger ? 'danger' : 'primary'} disabled={!canOk} onClick={() => close(isPrompt ? state.value : true)}>
                {state.okLabel || 'OK'}
              </Button>
            </>
          }
        >
          {state.body && <p style={{ margin: '0 0 10px', lineHeight: 1.5 }}>{state.body}</p>}
          {state.prompt ? (
            <div className="field">
              {state.label && <label>{state.label}</label>}
              <input
                className="input"
                autoFocus
                value={state.value}
                onChange={(e) => setState({ ...state, value: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && canOk && close(state.value)}
              />
            </div>
          ) : state.requireText ? (
            <div className="field">
              <label>
                {state.requireLabel || `Type "${state.requireText}" to confirm`}
              </label>
              <input
                className="input"
                autoFocus
                value={state.value}
                onChange={(e) => setState({ ...state, value: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && canOk && close(true)}
              />
            </div>
          ) : null}
        </Modal>
      )}
    </ConfirmCtx.Provider>
  );
}

/* ---- Toasts ---- */
const ToastCtx = createContext(() => {});
export const useToast = () => useContext(ToastCtx);

export function ToastProvider({ children }) {
  const [items, setItems] = useState([]);
  const push = useCallback((message, kind = 'info') => {
    const id = Math.random().toString(36).slice(2);
    setItems((s) => [...s, { id, message, kind }]);
    setTimeout(() => setItems((s) => s.filter((t) => t.id !== id)), 4000);
  }, []);
  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toast-stack">
        {items.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`}>
            {t.message}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

/* ---- Theme ---- */
export function useTheme() {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem('gq-theme') || 'light';
    } catch (e) {
      return 'light';
    }
  });
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('gq-theme', theme);
    } catch (e) {
      /* storage unavailable */
    }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))];
}
